import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { publicEndpoints } from "@/lib/api";
import { toast } from "sonner";
import { PublicHeader, PublicFooter, ChapterBadge } from "@/components/PublicChrome";
import {
  ArrowLeft,
  Heart,
  Trophy,
  Filter,
  ImageOff,
  MapPin,
} from "lucide-react";

const SOURCES = [
  { key: "", label: "Toutes" },
  { key: "DESSIN", label: "Dessin" },
  { key: "FIN_AVENTURE", label: "Fin d'aventure" },
  { key: "INSTAGRAM", label: "Instagram" },
];

export default function Galerie() {
  const [photos, setPhotos] = useState([]);
  const [challenge, setChallenge] = useState({ photos: [], moisAnnee: "" });
  const [filterSource, setFilterSource] = useState("");

  const reload = useCallback(async () => {
    try {
      const params = filterSource ? { source: filterSource } : {};
      const [list, c] = await Promise.all([
        publicEndpoints.galerie(params),
        publicEndpoints.challengeMois(),
      ]);
      setPhotos(list);
      setChallenge(c);
    } catch {
      toast.error("Erreur lors du chargement de la galerie");
    }
  }, [filterSource]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function voter(id) {
    try {
      const res = await publicEndpoints.voter(id);
      toast.success(
        res.deja ? "Vous aviez déjà voté" : `Vote enregistré (${res.total})`
      );
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  return (
    <div
      className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col"
      data-testid="galerie-page"
    >
      <PublicHeader current="galerie" />

      <main className="flex-1 px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto space-y-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-parchemin/50 hover:text-or text-sm transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Accueil
          </Link>

          {/* En-tête */}
          <div>
            <ChapterBadge testId="galerie-chapter">
              Chapitre III · Le grimoire
            </ChapterBadge>
            <h1 className="mt-5 font-display font-medium text-4xl md:text-5xl text-parchemin leading-[1.05] tracking-tight">
              Grimoire de la
              <br />
              <span className="italic text-or">communauté</span>
            </h1>
            <p className="mt-4 text-parchemin/60 text-base max-w-xl">
              Les traces laissées par ceux qui ont franchi le seuil avant vous —
              dessins, photos de fin d&apos;aventure, souvenirs partagés.
            </p>
          </div>

          {/* Challenge du mois */}
          {challenge.photos.length > 0 && (
            <section
              className="relative overflow-hidden border border-or/30 rounded-sm bg-gradient-to-br from-vert-900 via-noir to-noir p-6 md:p-8"
              data-testid="challenge-section"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-sm bg-or/10 border border-or/40 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-or" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
                    Challenge du mois
                  </div>
                  <div className="font-display text-xl text-parchemin mt-0.5">
                    {challenge.moisAnnee}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {challenge.photos.map((p, i) => (
                  <div
                    key={p.id}
                    className="group border border-or/20 hover:border-or/50 bg-noir-100/70 rounded-sm overflow-hidden transition-all duration-500"
                    data-testid={`challenge-${i}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={p.imageData}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-3 left-3 w-10 h-10 rounded-full bg-noir/80 backdrop-blur border border-or flex items-center justify-center font-display text-lg text-or stat-graved">
                        {i + 1}
                      </div>
                    </div>
                    <div className="p-4 flex items-center justify-between">
                      <span className="text-xs text-parchemin/70 truncate">
                        {p.joueurNom || "Anonyme"}
                      </span>
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] tracking-wider text-or">
                        <Heart className="w-3 h-3 fill-or" />
                        {p.votes}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Filtres */}
          <div
            className="flex flex-wrap items-center gap-3"
            data-testid="galerie-filters"
          >
            <div className="inline-flex items-center gap-2 pr-2 border-r border-or/20">
              <Filter className="w-3.5 h-3.5 text-or" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                Filtrer
              </span>
            </div>
            {SOURCES.map((s) => {
              const active = filterSource === s.key;
              return (
                <button
                  key={s.key || "all"}
                  onClick={() => setFilterSource(s.key)}
                  data-testid={`filter-${s.key ? s.key.toLowerCase() : "all"}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition-all duration-300 ${
                    active
                      ? "bg-or text-noir shadow-[0_6px_20px_-6px_rgba(200,146,42,0.5)]"
                      : "border border-or/25 text-parchemin/70 hover:border-or/60 hover:text-or"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Grille */}
          {photos.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-24 text-parchemin/40 border border-or/10 border-dashed rounded-sm"
              data-testid="galerie-empty"
            >
              <ImageOff className="w-8 h-8 mb-3" strokeWidth={1.2} />
              <p className="text-sm italic font-display">
                Aucune trace dans le grimoire pour le moment.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photos.map((p) => (
                <div
                  key={p.id}
                  className="group border border-or/15 hover:border-or/45 bg-noir-50/50 rounded-sm overflow-hidden transition-all duration-500 flex flex-col"
                  data-testid={`photo-${p.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.imageData}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-3 space-y-2 flex-1 flex flex-col">
                    <div className="text-[11px] text-parchemin/50 flex items-center gap-1.5">
                      <span className="truncate">
                        {p.joueurNom || "Anonyme"}
                      </span>
                      {p.ville && (
                        <>
                          <span className="text-or/40">·</span>
                          <span className="inline-flex items-center gap-0.5 truncate">
                            <MapPin className="w-2.5 h-2.5" />
                            {p.ville}
                          </span>
                        </>
                      )}
                    </div>
                    {p.caption && (
                      <p className="text-xs text-parchemin/70 line-clamp-2 italic font-display flex-1">
                        « {p.caption} »
                      </p>
                    )}
                    <button
                      onClick={() => voter(p.id)}
                      data-testid={`vote-${p.id}`}
                      className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono uppercase tracking-wider py-2 rounded-sm border border-or/25 hover:border-or hover:bg-or/10 text-or transition-all duration-300"
                    >
                      <Heart className="w-3 h-3" />
                      {p.votes || 0} votes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
